import { withOTLPSpan } from '@app/instrumentation/instrumentation.helpers';
import { CacheValue } from '@app/services/cache/cache.types';

type RedisClient = InstanceType<typeof import('ioredis').default>;
type RedisModule = {
    Redis: typeof import('ioredis').default;
};

const isLoggingEnabled = () => {
    const value = process.env.APP_CACHE_LOGGING;

    if (!value) {
        return false;
    }

    return ['1', 'true'].includes(value.toLowerCase());
};

const debugLog = (...args: unknown[]) => {
    if (isLoggingEnabled()) {
        console.debug(...args);
    }
};

const getRedisClient = async (): Promise<RedisClient | null> => {
    return withOTLPSpan('cache.redis.init', async span => {
        const redisUrl = process.env.REDIS_URL;
        const timeoutMs = Number(process.env.APP_CACHE_REDIS_TIMEOUT_MS) || 1000;

        if (!redisUrl) {
            span.end();

            return null;
        }

        // dirty hack to avoid importing ioredis on client side
        // due to lack of server/client side separation
        const { Redis }: RedisModule = eval('require')('ioredis');

        const client = new Redis(redisUrl, {
            lazyConnect: true,
            maxRetriesPerRequest: 1,
            connectTimeout: timeoutMs,
            commandTimeout: timeoutMs,
        });

        span.end();

        return client;
    });
};

export class RedisCacheService {
    readonly #redisPromise = getRedisClient();

    async get(key: string): Promise<{ value: CacheValue | null; hitStore?: string }> {
        const redis = await this.#redisPromise;

        if (!redis) {
            return { value: null };
        }

        return withOTLPSpan('cache.redis.get', async span => {
            span.setAttribute('cache.key', key);

            try {
                const value = await redis.get(key);

                span.setAttribute('cache.result', value === null ? 'miss' : 'hit');
                debugLog('[cache-redis] get', {
                    key,
                    result: value === null ? 'miss' : 'hit',
                });

                return { value };
            } catch (error) {
                span.setAttribute('cache.result', 'error');
                debugLog('[cache-redis] get:error', { key, error });

                return { value: null };
            } finally {
                span.end();
            }
        });
    }

    async set(key: string, value: CacheValue, ttlSeconds: number): Promise<void> {
        if (ttlSeconds <= 0) {
            return;
        }

        return withOTLPSpan('cache.redis.set', async span => {
            span.setAttribute('cache.key', key);
            span.setAttribute('cache.ttl_seconds', ttlSeconds);

            try {
                const redis = await this.#redisPromise;

                if (!redis) {
                    return;
                }

                await redis.set(key, value, 'EX', ttlSeconds);

                debugLog('[cache-redis] set', {
                    key,
                    ttlSeconds,
                });
            } catch (error) {
                span.setAttribute('cache.result', 'error');
                debugLog('[cache-redis] set:error', { key, ttlSeconds, error });
            } finally {
                span.end();
            }
        });
    }
}
export const appCache = new RedisCacheService();
